import axios from "axios";
import liff from "@line/liff";

const API_BASE_URL = process.env.REACT_APP_BIGC_BIGLINE_API_URL;
const API_KEY = process.env.REACT_APP_BIGC_BIGLINE_API_KEY || "RE1LVC1CaWdDLUludGVybmFs";
const IS_PROD = process.env.REACT_APP_ENV === 'prod';
const log = (...args) => { if (!IS_PROD) console.log(...args); };
const warn = (...args) => { if (!IS_PROD) console.warn(...args); };

// สร้าง axios instance (pattern เดียวกับ fortuneCardApi)
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "x-api-key": API_KEY,
  },
});

// Request interceptor — ดึง token จาก localStorage + log ทุก request
api.interceptors.request.use(
  (config) => {
    const lineMid = localStorage.getItem("userId");
    if (lineMid) {
      config.headers["x-line-mid"] = lineMid;
    }

    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      config.headers["Authorization"] = `Bearer ${authToken}`;
    }

    const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
    if (clientAuth) {
      config.headers["X-Client-Auth"] = clientAuth;
    }

    config.headers["Accept-Language"] = (localStorage.getItem("i18nextLng") || "TH").toUpperCase();
    config.headers["x-api-key"] = API_KEY;

    // ✅ Console.log ทุก request
    log(`[BTS API] >>> ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    log("[BTS API] >>> Headers:", {
      "x-line-mid": config.headers["x-line-mid"],
      "Authorization": config.headers["Authorization"] ? "Bearer ***" : "none",
      "x-api-key": config.headers["x-api-key"] ? "***" : "none",
    });
    if (config.data) {
      log("[BTS API] >>> Body:", config.data);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — unwrap response.data + log ทุก response
api.interceptors.response.use(
  (response) => {
    log(`[BTS API] <<< ${response.status} ${response.config?.url}`);
    log("[BTS API] <<< Data:", response.data);
    return response.data;
  },
  (error) => {
    if (error.response) {
      console.error(`[BTS API] <<< ERROR ${error.response.status} ${error.config?.url}`);
      console.error("[BTS API] <<< Error Data:", error.response.data);

      const errorData = error.response.data;
      const customError = new Error(
        errorData?.message || errorData?.error || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง"
      );
      customError.response = error.response;
      customError.status = error.response.status;
      customError.data = errorData;
      throw customError;
    } else if (error.request) {
      console.error("[BTS API] <<< NETWORK ERROR:", error.message);
      const networkError = new Error("ไม่สามารถเชื่อมต่อได้ กรุณาตรวจสอบอินเทอร์เน็ต");
      networkError.isNetworkError = true;
      throw networkError;
    } else {
      console.error("[BTS API] <<< UNKNOWN ERROR:", error.message);
      throw new Error(error.message);
    }
  }
);

// =====================================================
// BTS API — ทุก endpoint ของ game-bts
// =====================================================

export const btsApi = {

  // ---------- Player ----------

  initPlayer: async () => {
    let lineMid = localStorage.getItem("userId");
    let displayName = localStorage.getItem("line_display_name");
    let pictureUrl = localStorage.getItem("line_picture_url");
    let bigCardNumber = localStorage.getItem("bigCardNumber");

    // refresh profile จาก LIFF (กัน localStorage stale) — ถ้า LIFF ใช้ไม่ได้ก็ fall back
    try {
      if (liff && typeof liff.isLoggedIn === "function" && liff.isLoggedIn()) {
        const profile = await liff.getProfile();
        if (profile) {
          if (profile.userId) { lineMid = profile.userId; localStorage.setItem("userId", profile.userId); }
          if (profile.displayName) { displayName = profile.displayName; localStorage.setItem("line_display_name", profile.displayName); }
          if (profile.pictureUrl) { pictureUrl = profile.pictureUrl; localStorage.setItem("line_picture_url", profile.pictureUrl); }
        }
      }
    } catch (e) { warn("[BTS API] LIFF getProfile failed:", e.message); }

    // ถ้ายังไม่มี bigCardNumber → ดึงจาก API
    if (!bigCardNumber || bigCardNumber === "BTS_AUTH_OK") {
      try {
        const authToken = localStorage.getItem("authToken");
        const clientAuth = process.env.REACT_APP_CLIENT_AUTH;
        const userRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/current`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "X-Client-Auth": clientAuth,
              "x-user-id": lineMid,
            },
          }
        );
        const fetchedCard = userRes.data?.data?.bigCardNumber;
        if (fetchedCard) {
          bigCardNumber = fetchedCard;
          localStorage.setItem("bigCardNumber", fetchedCard);
          log("[BTS API] Fetched bigCardNumber from API:", fetchedCard);
        }
      } catch (e) {
        warn("[BTS API] Failed to fetch bigCardNumber:", e.message);
      }
    }

    log("[BTS API] initPlayer — lineMid:", lineMid, "displayName:", displayName, "bigCardNumber:", bigCardNumber);

    // init ใน game-bigpoint (ใช้ร่วมกับเกมอื่น)
    const bigpointResult = await api.post("/game-bigpoint/init", {
      lineId: lineMid,
      displayName: displayName || null,
      pictureUrl: pictureUrl || null,
      bigpointId: bigCardNumber || null,
    });
    log("[BTS API] initPlayer — bigpoint result:", bigpointResult);

    // init ใน game-bts
    const btsResult = await api.post("/game-bts/init", {
      displayName: displayName || null,
      pictureUrl: pictureUrl || null,
    });
    log("[BTS API] initPlayer — bts result:", btsResult);

    return btsResult;
  },

  // ---------- Dashboard ----------

  getDashboard: async () => {
    log("[BTS API] getDashboard — calling...");
    const result = await api.get("/game-bts/dashboard");
    log("[BTS API] getDashboard — result:", result);
    return result;
  },

  getCampaignStatus: async () => {
    log("[BTS API] getCampaignStatus — calling...");
    const result = await api.get("/game-bts/status");
    log("[BTS API] getCampaignStatus — result:", result);
    return result;
  },

  // ---------- Party (กลุ่ม) ----------

  createParty: async (partyName) => {
    log("[BTS API] createParty — partyName:", partyName);
    const result = await api.post("/game-bts/party/create", { partyName });
    log("[BTS API] createParty — result:", result);
    return result;
  },

  joinParty: async (partyCode) => {
    log("[BTS API] joinParty — partyCode:", partyCode);
    const result = await api.post("/game-bts/party/join", { partyCode });
    log("[BTS API] joinParty — result:", result);
    return result;
  },

  getMyParty: async () => {
    log("[BTS API] getMyParty — calling...");
    const result = await api.get("/game-bts/party/me");
    log("[BTS API] getMyParty — result:", result);
    return result;
  },

  getPartyStatus: async (partyCode) => {
    log("[BTS API] getPartyStatus — partyCode:", partyCode);
    const result = await api.get(`/game-bts/party/${partyCode}/status`);
    log("[BTS API] getPartyStatus — result:", result);
    return result;
  },

  // ---------- Receipt (ใบเสร็จ) ----------

  // params จาก QR link: customer, receiptNo, partnerId, sale, sale_date, sale_store
  scanReceipt: async ({ receiptNo, partnerId, customer, sale, sale_date, sale_store }) => {
    log("[BTS API] scanReceipt —", { receiptNo, partnerId, customer, sale, sale_date, sale_store });
    const result = await api.post("/game-bts/receipt/scan", {
      receiptNo,
      partnerId,
      customer,
      sale,
      sale_date,
      sale_store,
    });
    log("[BTS API] scanReceipt — result:", result);
    return result;
  },

  getReceiptHistory: async () => {
    log("[BTS API] getReceiptHistory — calling...");
    const result = await api.get("/game-bts/receipt/history");
    log("[BTS API] getReceiptHistory — result:", result);
    return result;
  },

  // ---------- Ranking ----------

  getRanking: async (limit = 20) => {
    log("[BTS API] getRanking — limit:", limit);
    const result = await api.get(`/game-bts/ranking?limit=${limit}`);
    log("[BTS API] getRanking — result:", result);
    return result;
  },

  getMyTeamRanking: async () => {
    log("[BTS API] getMyTeamRanking — calling...");
    const result = await api.get("/game-bts/ranking/my-team");
    log("[BTS API] getMyTeamRanking — result:", result);
    return result;
  },
};

export default btsApi;
